import type { Fraction } from '@mimir/common/util/fractionType';
import type { ItemSegmentMessageFromWorker, ItemSegmentResponse, ItemSegmentRequest, ItemSegmentMessageFromMain } from '../protocol/itemSegmentProtocol';
import type { SegmentProvider } from '@mimir/cutter/frameProvider/demuxAndDecode/segmentedPacketProvider';
import type { PlaybackQuality } from '@mimir/common/types/playbackQuality';

export type CreateSegmentProvider = (itemId: string, requestedQuality: PlaybackQuality | null, ignoreVideo: boolean, ignoreAudio: boolean) => SegmentProvider;

export function createItemSegmentMainHandler(
  createSegmentProvider: CreateSegmentProvider,
  postMessageToWorker: (message: ItemSegmentMessageFromMain, transfer?: Transferable[]) => void,
): (message: ItemSegmentMessageFromWorker) => void {
  const segmentProviders = new Map<number, SegmentProvider>();

  const createSegmentProviderInternal = (segmentProviderId: number, itemId: string, requestedQuality: PlaybackQuality | null, ignoreVideo: boolean, ignoreAudio: boolean) => {
    if (segmentProviders.has(segmentProviderId)) {
      throw new Error(`Segment provider with id: ${segmentProviderId} already exists`);
    }

    const segmentProvider = createSegmentProvider(itemId, requestedQuality, ignoreVideo, ignoreAudio);
    segmentProvider.addDurationChangedListener((duration: Fraction) => {
      postMessageToWorker({ type: 'duration_changed', segmentProviderId, duration });
    });
    segmentProviders.set(segmentProviderId, segmentProvider);
  };

  const closeSegmentProvider = (segmentProviderId: number) => {
    const segmentProvider = segmentProviders.get(segmentProviderId);
    if (segmentProvider !== undefined) {
      segmentProvider.close();
      segmentProviders.delete(segmentProviderId);
      return;
    }
    throw new Error(`No such segment provider: ${segmentProviderId}`);
  };

  const getSegmentProvider = (segmentProviderId: number): SegmentProvider => {
    const segmentProvider = segmentProviders.get(segmentProviderId);
    if (segmentProvider !== undefined) {
      return segmentProvider;
    }
    throw new Error(`No such segment provider: ${segmentProviderId}`);
  };

  const postResponseToWorker = (requestId: number, response: ItemSegmentResponse): void => {
    postMessageToWorker({ type: 'response', requestId, response });
  };

  const postErrorToWorker = (requestId: number, error: any): void => {
    const { message, name, stack } = error;
    postResponseToWorker(requestId, { type: 'error', message, name, stack });
  };

  const handleRequestFromWorker = (requestId: number, request: ItemSegmentRequest) => {
    const requestType = request.type;
    switch (requestType) {
      case 'get_segment_info_at': {
        const segmentProvider = getSegmentProvider(request.segmentProviderId);
        segmentProvider.getSegmentInfoAt(request.position)
          .then((segmentInfo) => {
            postResponseToWorker(requestId, { type: 'segment_info', segmentInfo });
          })
          .catch((error) => {
            postErrorToWorker(requestId, error);
          });
        break;
      }
      case 'get_segment': {
        const segmentProvider = getSegmentProvider(request.segmentProviderId);
        segmentProvider.getSegment(request.id)
          .then((encodedData) => {
            postResponseToWorker(requestId, { type: 'segment', encodedData });
          })
          .catch((error) => {
            postErrorToWorker(requestId, error);
          });
        break;
      }
      case 'get_duration': {
        const segmentProvider = getSegmentProvider(request.segmentProviderId);
        segmentProvider.getDuration()
          .then((duration) => {
            postResponseToWorker(requestId, { type: 'duration', duration });
          })
          .catch((error) => {
            postErrorToWorker(requestId, error);
          });
        break;
      }
      case 'get_virtual_clip_info': {
        const segmentProvider = getSegmentProvider(request.segmentProviderId);
        segmentProvider.getVirtualClipInfo()
          .then((virtualClipInfo) => {
            postResponseToWorker(requestId, { type: 'virtual_clip_info', virtualClipInfo });
          })
          .catch((error) => {
            postErrorToWorker(requestId, error);
          });
        break;
      }
      case 'get_current_quality': {
        const segmentProvider = getSegmentProvider(request.segmentProviderId);
        segmentProvider.getCurrentQuality()
          .then((currentQuality) => {
            postResponseToWorker(requestId, { type: 'current_quality', currentQuality });
          })
          .catch((error) => {
            postErrorToWorker(requestId, error);
          });
        break;
      }
      case 'get_available_qualities': {
        const segmentProvider = getSegmentProvider(request.segmentProviderId);
        segmentProvider.getAvailableQualities()
          .then((availableQualities) => {
            postResponseToWorker(requestId, { type: 'available_qualities', availableQualities });
          })
          .catch((error) => {
            postErrorToWorker(requestId, error);
          });
        break;
      }
      default:
        requestType satisfies never;
    }
  };

  return (message: ItemSegmentMessageFromWorker) => {
    const messageType = message.type;
    switch (messageType) {
      case 'request': {
        handleRequestFromWorker(message.requestId, message.request);
        break;
      }
      case 'create_segment_provider': {
        createSegmentProviderInternal(message.segmentProviderId, message.itemId, message.requestedQuality, message.ignoreVideo, message.ignoreAudio);
        break;
      }
      case 'close_segment_provider': {
        closeSegmentProvider(message.segmentProviderId);
        break;
      }
      default:
        messageType satisfies never;
    }
  };
}
